// Canvas 2D renderer.
//
// This is the ONLY place trig is allowed — see the note in engine/vec2.ts. Nothing in here may feed
// a value back into the simulation.
//
// Aesthetic (PLAN.md § M8½): a muted Australian paddock — dry summer grass, weathered timber, dirty
// white sheep, a black-and-white dog. Flat colour, no texture, no sprites; the marks stay geometric
// and only the palette carries the place.
//
// EVERY COLOUR BELOW WAS CHOSEN BY MEASUREMENT, not by eye. Marks here are 3-5 px on a phone, and a
// muted palette is one careless step from illegible — the previous amber-on-black was violently
// legible and set a bar that "tasteful" can quietly fail. Each pair that a player must tell apart
// clears the WCAG 3:1 non-text floor, and two lessons came out of getting there:
//
//   * A pale paddock cannot carry white sheep — cream on straw measured 1.94:1. The sheep outline
//     does the work instead, at 9.2:1, which is why calm sheep are outlined rather than plain.
//   * Hue is not contrast. A red-oxide gate on a green paddock looked obviously different and
//     measured 1.03:1, because the two had almost identical luminance. The gate is dark eucalypt on
//     pale straw for that reason and no other.

import type { Simulation } from '../engine/sim'
import type { WorldMapper } from '../ui/input'

/** Breathing room around the paddock, in CSS px, so the perimeter fence is fully drawn. */
const PADDING_PX = 14

/** Dry patches worked into the ground, so the paddock is not one flat slab. */
const GROUND_PATCH = '#CEBF80'
const GROUND_BARE = '#C4B172'
/** Pigment that drifted while the wash was wet. Sage on one side, ochre on the other. */
const WASH_SAGE = '#A8B184'
const WASH_OCHRE = '#C79A54'
/** Everything standing on the ground casts one. This is what stops the marks looking like stickers. */
const SHADOW = 'rgba(60, 48, 26, 0.28)'
/** Fence posts. Darker than the rail so the line reads as timber rather than as a drawn stroke. */
const POST = '#3A3222'

/** Beyond the fence: eucalypt shadow. Dimmer than the paddock so the playfield reads as the subject.
    Re-measured when the shell went to paper — 4.29:1 against the paddock, up from the olive's 4.04. */
const BG = '#4F5B45'
/** The paddock: dry summer grass. Pale on purpose — it is what the dog and the fences read against. */
const GROUND = '#D6C88F'
/**
 * Animals are drawn as BODIES, not dots: an ellipse along the way the animal is pointing.
 *
 * Two measurements forced this. A ewe is roughly 0.75 m long and 0.45 m wide and a kelpie 0.60 by
 * 0.20, so a disc says nothing true about either — and drawn as discs the dog was only 1.36× smaller
 * than a sheep by radius when the real difference is closer to twice the visible mass.
 *
 * The other is the pile-up. Real bodies now overlap on about 1 % of ticks (§ M12 pass 2), but the
 * DRAWN marks overlapped on 83 %, because at a phone's ~5 px/m a sheep was floored at a 6 px radius
 * — a 1.2 m body against a real 0.4 m — so a mob at its ordinary 1.56 m spacing was always a heap.
 * What fixes that is not a smaller mark but a NARROWER one: sheep stand shoulder to shoulder, so it
 * is the cross-axis that decides whether they merge. At a 3.6 px half-width two abreast need 1.44 m
 * and have 1.56, while remaining 12 px long and perfectly legible.
 *
 * Semi-axes in world metres, along the animal's heading and across it.
 */
const SHEEP_LEN_M = 0.375
const SHEEP_WID_M = 0.225
const DOG_LEN_M = 0.3
const DOG_WID_M = 0.1

/**
 * Floors on mark size, in CSS px, because a 70×120 m world on a 390 px phone is ~5 px/m and a
 * true-scale sheep would be 2 px. Marks must stay legible independent of zoom.
 *
 * The dog stays the smaller animal — a border collie is smaller than a ewe, and the sizes used to
 * have it backwards. At these floors it is 10.4 × 5.2 px against a sheep's 12 × 7.2, so about half
 * the visible mass, which is roughly the real difference in plan view.
 *
 * It is not cut finer than that, and the first attempt was: at a 4.4 px width the dog became a dark
 * sliver, and it is the one mark the player is steering. § M8¾ measured the blaze at 5.8:1 against a
 * frightened mob and the body alone at 2.9:1, so the blaze is what makes the dog findable — and
 * below about 4 px of width the stripe closes up and takes the facing with it.
 */
const MIN_SHEEP_LEN_CSS = 6.0
const MIN_SHEEP_WID_CSS = 3.6
const MIN_DOG_LEN_CSS = 5.2
const MIN_DOG_WID_CSS = 2.6
/** Weathered timber. */
const FENCE = '#4A3F2E'
const SHEEP_CALM = '#FFFDF7'
/**
 * A frightened sheep goes DARK, not bright, and that inversion is forced by the arithmetic rather
 * than chosen. Calm sheep must be pale to read as sheep; stressed sheep must differ from both the
 * paddock AND from calm sheep. On a pale ground only a dark mark satisfies both — 3.8:1 against the
 * grass and 6.3:1 against a calm sheep.
 */
const SHEEP_PANIC = '#A83A1C'
/** Outline. This, not the fill, is what makes a near-white sheep visible on pale grass. */
const SHEEP_EDGE = '#2A2419'
/** Dark eucalypt. Chosen for luminance, not for hue — see the header note. */
const GATE = '#2E4A33'
/** Border collie. */
const DOG = '#141414'
const DOG_BLAZE = '#F7F3E8'

export class PaddockRenderer implements WorldMapper {
  private ctx: CanvasRenderingContext2D
  private scale = 1
  private offsetX = 0
  private offsetY = 0
  private dpr = 1
  /** Last heading with real motion behind it, so a stopped dog does not spin. Held as a vector. */
  private dogHeadingX = 0
  private dogHeadingY = 1
  /**
   * The paddock surface, drawn once per resize instead of every frame.
   *
   * The ground has a few hundred patches on it and none of them move. Re-rasterising that every
   * frame would be the most expensive thing in the renderer and would buy exactly nothing; the
   * budget is 200 sheep at 60 Hz on a mid-range phone.
   */
  private ground: HTMLCanvasElement | null = null

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly world: { w: number; h: number },
  ) {
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('2D canvas context unavailable')
    this.ctx = ctx
    this.resize()
    new ResizeObserver(() => this.resize()).observe(canvas)
  }

  /** Fit the world into the canvas, letterboxed, at devicePixelRatio. */
  resize(): void {
    const rect = this.canvas.getBoundingClientRect()
    this.dpr = Math.min(window.devicePixelRatio || 1, 2)
    this.canvas.width = Math.round(rect.width * this.dpr)
    this.canvas.height = Math.round(rect.height * this.dpr)

    // Inset so the perimeter fence is not drawn flush against the canvas edge, where it gets
    // clipped to half a line width and reads as a missing wall.
    const pad = PADDING_PX
    this.scale = Math.min(
      Math.max(rect.width - pad * 2, 1) / this.world.w,
      Math.max(rect.height - pad * 2, 1) / this.world.h,
    )
    this.ground = null // rebuilt lazily at the new size
    this.offsetX = (rect.width - this.world.w * this.scale) / 2
    this.offsetY = (rect.height - this.world.h * this.scale) / 2
  }

  toWorld(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect()
    return {
      x: (clientX - rect.left - this.offsetX) / this.scale,
      y: (clientY - rect.top - this.offsetY) / this.scale,
    }
  }

  private sx(x: number): number {
    return (this.offsetX + x * this.scale) * this.dpr
  }

  private sy(y: number): number {
    return (this.offsetY + y * this.scale) * this.dpr
  }

  /**
   * Paint the paddock surface into an offscreen canvas.
   *
   * A flat slab of one colour is what made the field look like a spreadsheet: there is nothing for
   * the eye to measure motion against, so the sheep appear to slide rather than travel. Dry patches
   * and mown lines give it grain — and, more usefully, they give the flock a background it visibly
   * moves over.
   *
   * The pattern is a HASH OF POSITION rather than a random draw. It has to be identical every time
   * the canvas is rebuilt, or the paddock would reshuffle on every rotate and resize; and it must
   * never touch the simulation's RNG, which belongs to the puzzle. This is a pure function of the
   * world rect, so it is stable without being seeded.
   */
  private buildGround(w: number, h: number): HTMLCanvasElement {
    const c = document.createElement('canvas')
    c.width = Math.max(1, Math.round(w))
    c.height = Math.max(1, Math.round(h))
    const g = c.getContext('2d')!
    g.fillStyle = GROUND
    g.fillRect(0, 0, c.width, c.height)

    // Mown lines: very low contrast, just enough to read as a worked paddock rather than a fill.
    const band = Math.max(18, c.height / 26)
    g.fillStyle = GROUND_PATCH
    // Faint. At 0.5 these read as hard stripes over the wash — a machine pattern, and the one mark
    // watercolour never makes. They are meant to suggest a worked paddock, not to be seen.
    g.globalAlpha = 0.18
    for (let y = 0; y < c.height; y += band * 2) g.fillRect(0, y, c.width, band)
    g.globalAlpha = 1

    // Bare patches. Integer hashing so the layout is deterministic and evenly spread without any
    // trig, and without a PRNG that would need seeding and storing.
    const cell = Math.max(24, Math.round(Math.min(c.width, c.height) / 14))
    for (let gy = 0; gy * cell < c.height + cell; gy++) {
      for (let gx = 0; gx * cell < c.width + cell; gx++) {
        let n = (gx * 73856093) ^ (gy * 19349663)
        n = (n ^ (n >>> 13)) >>> 0
        if (n % 5 !== 0) continue
        const px = gx * cell + ((n >>> 3) % cell)
        const py = gy * cell + ((n >>> 11) % cell)
        const rr = cell * (0.18 + ((n >>> 17) % 100) / 400)
        g.fillStyle = n % 10 === 0 ? GROUND_BARE : GROUND_PATCH
        g.globalAlpha = 0.55
        g.beginPath()
        g.ellipse(px, py, rr, rr * 0.62, 0, 0, Math.PI * 2)
        g.fill()
      }
    }
    g.globalAlpha = 1

    // Pigment blooms. A flat wash is the thing that most says "fill" rather than "paint", so two
    // hues are drifted through it — sage and ochre — at an alpha low enough that neither becomes a
    // shape you could mistake for terrain. Same integer hash as the bare patches: deterministic
    // layout, no PRNG to seed and store.
    // Small and many. At a fifth of the paddock they were soft grey discs — the picture read as
    // mould rather than pigment. Drift is made of many small pools, not a few big ones.
    const bloom = Math.max(24, Math.min(c.width, c.height) / 11)
    for (let i = 0; i < 26; i++) {
      let n = (i * 2654435761) >>> 0
      n = (n ^ (n >>> 15)) >>> 0
      const bx = (n % 1000) / 1000 * c.width
      const by = ((n >>> 10) % 1000) / 1000 * c.height
      const rr = bloom * (0.45 + ((n >>> 20) % 100) / 90)
      const grd = g.createRadialGradient(bx, by, 0, bx, by, rr)
      grd.addColorStop(0, i % 2 === 0 ? WASH_SAGE : WASH_OCHRE)
      grd.addColorStop(1, 'transparent')
      g.globalAlpha = 0.1
      g.fillStyle = grd
      g.beginPath()
      g.ellipse(bx, by, rr, rr * 0.78, 0, 0, Math.PI * 2)
      g.fill()
    }
    g.globalAlpha = 1

    // Edge pooling. Watercolour dries darkest where the wash met the edge of the paper, and this is
    // the single most recognisable thing it does — without it the blooms above just read as dirt.
    // Painted as four inward-fading gradients so the corners double up, which is what really happens.
    const pool = Math.max(10, Math.min(c.width, c.height) / 22)
    const edges: [number, number, number, number, number, number][] = [
      [0, 0, 0, pool, c.width, pool],
      [0, c.height, 0, c.height - pool, c.width, pool],
      [0, 0, pool, 0, pool, c.height],
      [c.width, 0, c.width - pool, 0, pool, c.height],
    ]
    for (const [x0, y0, x1, y1, ew, eh] of edges) {
      const grd = g.createLinearGradient(x0, y0, x1, y1)
      grd.addColorStop(0, 'rgba(122, 106, 62, 0.42)')
      grd.addColorStop(1, 'transparent')
      g.fillStyle = grd
      g.fillRect(Math.min(x0, x1), Math.min(y0, y1), ew, eh)
    }

    // Granulation: the grain of coarse pigment settling into the tooth of the paper. Two passes of
    // the same hash at different scales, so it does not tile visibly.
    for (const [step, alpha, tint] of [[3, 0.05, '#8C7A46'], [7, 0.04, '#FFF6DC']] as const) {
      g.fillStyle = tint
      g.globalAlpha = alpha
      for (let y = 0; y < c.height; y += step) {
        for (let x = 0; x < c.width; x += step) {
          let n = (x * 374761393 + y * 668265263) >>> 0
          n = (n ^ (n >>> 13)) >>> 0
          if (n % 11 === 0) g.fillRect(x, y, step - 1, step - 1)
        }
      }
    }
    g.globalAlpha = 1
    return c
  }

  /**
   * A soft ellipse under an animal. The cheapest thing that stops marks looking like stickers.
   *
   * Drawn round rather than aligned to the body: a shadow is cast by a light source, not by the
   * animal's facing, and a rotating shadow reads as the ground moving.
   */
  private shadow(x: number, y: number, r: number): void {
    const ctx = this.ctx
    ctx.fillStyle = SHADOW
    ctx.beginPath()
    ctx.ellipse(x + r * 0.22, y + r * 0.42, r * 1.02, r * 0.66, 0, 0, Math.PI * 2)
    ctx.fill()
  }

  /**
   * An animal, as an ellipse pointing the way it is pointing.
   *
   * The rotation comes from the heading VECTOR through the canvas transform — `transform(hx, hy,
   * −hy, hx, …)` is exactly the rotation matrix for a unit vector — rather than from `Math.atan2`.
   * Trig is permitted in the renderer and nothing here flows back into the simulation, so this is
   * taste rather than law: forming an angle only to have the canvas turn it straight back into a
   * matrix is work that can go wrong at the ±180° seam for no benefit.
   */
  private body(x: number, y: number, hx: number, hy: number, a: number, b: number): void {
    const ctx = this.ctx
    ctx.save()
    ctx.translate(x, y)
    ctx.transform(hx, hy, -hy, hx, 0, 0)
    ctx.beginPath()
    ctx.ellipse(0, 0, a, b, 0, 0, Math.PI * 2)
    ctx.restore()
  }

  render(sim: Simulation): void {
    const ctx = this.ctx
    ctx.fillStyle = BG
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)

    // Paddock ground — a barely-lifted panel so the play area reads as a distinct surface. Without
    // it a square world in a portrait viewport is indistinguishable from the dead space around it.
    const groundX = this.sx(0)
    const groundY = this.sy(0)
    const groundW = this.world.w * this.scale * this.dpr
    const groundH = this.world.h * this.scale * this.dpr
    if (!this.ground || this.ground.width !== Math.round(groundW)) {
      this.ground = this.buildGround(groundW, groundH)
    }
    ctx.drawImage(this.ground, groundX, groundY)

    // Everything in world space is clipped to the paddock, so the dog's repulsion ring does not
    // bleed out over the surrounding dead space.
    ctx.save()
    ctx.beginPath()
    ctx.rect(groundX, groundY, groundW, groundH)
    ctx.clip()

    // Gate
    const g = sim.level.gate
    ctx.strokeStyle = GATE
    ctx.lineWidth = 1 * this.dpr
    ctx.setLineDash([4 * this.dpr, 4 * this.dpr])
    ctx.strokeRect(this.sx(g.x), this.sy(g.y), g.w * this.scale * this.dpr, g.h * this.scale * this.dpr)
    ctx.setLineDash([])

    // Fences — timber, so heavier than the 1px vector line this used to be.
    ctx.strokeStyle = FENCE
    ctx.lineWidth = 1.75 * this.dpr
    ctx.lineCap = 'round'
    ctx.beginPath()
    for (const f of sim.level.fences) {
      ctx.moveTo(this.sx(f.a.x), this.sy(f.a.y))
      ctx.lineTo(this.sx(f.b.x), this.sy(f.b.y))
    }
    ctx.stroke()

    // Posts. A bare stroke reads as a drawn line; posts read as something built and standing in a
    // field, and they give the fence a direction the eye can follow.
    ctx.fillStyle = POST
    const postR = Math.max(1.4 * this.dpr, 0.16 * this.scale * this.dpr)
    const spacing = Math.max(10 * this.dpr, 3.5 * this.scale * this.dpr)
    for (const f of sim.level.fences) {
      const ax = this.sx(f.a.x)
      const ay = this.sy(f.a.y)
      const bx = this.sx(f.b.x)
      const by = this.sy(f.b.y)
      const len = Math.hypot(bx - ax, by - ay)
      const n = Math.max(1, Math.round(len / spacing))
      for (let i = 0; i <= n; i++) {
        const t = i / n
        ctx.beginPath()
        ctx.arc(ax + (bx - ax) * t, ay + (by - ay) * t, postR, 0, Math.PI * 2)
        ctx.fill()
      }
    }

    // Sheep — outlined, because the fill alone cannot carry them on pale grass.
    //
    // The old version faded opacity with stress, which on a black ground was atmosphere and on this
    // one is a contrast leak: a calm sheep at 0.55 alpha is half-mixed into the paddock behind it,
    // undoing the ratio the palette was chosen for. Stress is carried by colour alone now.
    const sheepA = Math.max(MIN_SHEEP_LEN_CSS * this.dpr, SHEEP_LEN_M * this.scale * this.dpr)
    const sheepB = Math.max(MIN_SHEEP_WID_CSS * this.dpr, SHEEP_WID_M * this.scale * this.dpr)
    ctx.lineWidth = 1 * this.dpr
    ctx.strokeStyle = SHEEP_EDGE
    for (const s of sim.sheep) this.shadow(this.sx(s.pos.x), this.sy(s.pos.y), sheepB)
    for (const s of sim.sheep) {
      ctx.fillStyle = s.panicked ? SHEEP_PANIC : SHEEP_CALM
      // Pointed the way the animal is pointing — the simulation has carried a heading since sheep
      // stopped being able to strafe, and a mob all facing one way is the clearest reading of
      // "these are being driven" the renderer can give.
      this.body(this.sx(s.pos.x), this.sy(s.pos.y), s.heading.x, s.heading.y, sheepA, sheepB)
      ctx.fill()
      ctx.stroke()
    }

    // Dog — ring shows the current repulsor radius, widening on a bark.
    const dogR = sim.config.dogRadius * (sim.barking ? sim.config.barkRadiusMultiplier : 1)
    ctx.strokeStyle = DOG
    ctx.globalAlpha = sim.barking ? 0.5 : 0.18
    ctx.beginPath()
    ctx.arc(this.sx(sim.dog.x), this.sy(sim.dog.y), dogR * this.scale * this.dpr, 0, Math.PI * 2)
    ctx.stroke()
    ctx.globalAlpha = 1

    this.drawCollie(sim)

    ctx.restore()
  }

  /**
   * The dog: a black circle with a white blaze, aligned to the way it is travelling.
   *
   * The blaze is not decoration. It does two jobs the plain dot could not: it makes the dog readable
   * against a mob of dark, frightened sheep (the body alone measures only 2.9:1 against them, while
   * the blaze is 5.8:1), and it shows FACING — which the player previously had to infer from motion.
   *
   * It is inset inside a dark rim rather than crossing the whole circle, so the white never touches
   * the pale paddock; blaze against grass is 1.5:1 and would smear the dog's outline into the field.
   *
   * Trig is fine here and nowhere else: this is the renderer, and nothing computed here goes back
   * into the simulation.
   */
  private drawCollie(sim: Simulation): void {
    const ctx = this.ctx
    const x = this.sx(sim.dog.x)
    const y = this.sy(sim.dog.y)
    const a = Math.max(MIN_DOG_LEN_CSS * this.dpr, DOG_LEN_M * this.scale * this.dpr)
    const b = Math.max(MIN_DOG_WID_CSS * this.dpr, DOG_WID_M * this.scale * this.dpr)

    // Heading. A stationary dog keeps the last one rather than snapping to a default, so the mark
    // does not spin when the dog stops. Kept as a vector, so the body and the blaze share one
    // rotation and neither has to form an angle.
    const vx = sim.dogVel.x
    const vy = sim.dogVel.y
    const speed = Math.sqrt(vx * vx + vy * vy)
    if (speed > 0.05) {
      this.dogHeadingX = vx / speed
      this.dogHeadingY = vy / speed
    }
    const hx = this.dogHeadingX
    const hy = this.dogHeadingY

    this.shadow(x, y, b * 1.4)
    ctx.fillStyle = DOG
    this.body(x, y, hx, hy, a, b)
    ctx.fill()

    ctx.save()
    ctx.translate(x, y)
    ctx.transform(hx, hy, -hy, hx, 0, 0)
    // Clip to the body so the blaze stops short of its edge.
    ctx.beginPath()
    ctx.ellipse(0, 0, a * 0.78, b * 0.78, 0, 0, Math.PI * 2)
    ctx.clip()
    ctx.fillStyle = DOG_BLAZE
    // Down the muzzle, ALONG the heading — a collie's blaze runs front-to-back, and a band across
    // the body would read as a belt and say nothing about which way the dog is pointing.
    ctx.fillRect(-a * 0.2, -b * 0.5, a * 1.2, b)
    ctx.restore()
  }
}
